<template>
  <section class="report-page">
    <div class="hero">
      <div>
        <h1>举报处理</h1>
        <p>用户在商品详情页提交的举报会进入这里。核实后可下架商品或忽略举报。</p>
      </div>
      <el-button type="primary" icon="el-icon-refresh" size="small" :loading="loading" @click="reload">刷新列表</el-button>
    </div>

    <div class="stat-row">
      <div v-for="card in statCards" :key="card.key" class="stat-card" :class="card.key" @click="switchTab(card.tab)">
        <span class="stat-label">{{ card.label }}</span>
        <strong class="stat-value">{{ stats[card.key] }}</strong>
        <span class="stat-note">{{ card.note }}</span>
      </div>
    </div>

    <el-card shadow="never" class="table-card">
      <div class="toolbar">
        <div class="tabs">
          <span v-for="tab in tabs" :key="tab.value" :class="['tab', { active: statusFilter === tab.value }]" @click="switchTab(tab.value)">
            {{ tab.label }}<em v-if="tab.value === '待处理' && stats.pending">{{ stats.pending }}</em>
          </span>
        </div>
        <el-input v-model="keyword" clearable size="small" placeholder="搜索商品名称 / 举报人" prefix-icon="el-icon-search" style="width: 280px" />
      </div>

      <el-table v-loading="loading" :data="pagedRows" border stripe empty-text="暂无举报记录">
        <el-table-column label="商品" min-width="230">
          <template slot-scope="scope">
            <div class="goods-cell">
              <img v-if="scope.row.goodsImage" :src="scope.row.goodsImage" class="thumb" @click="previewImg(scope.row.goodsImage)" />
              <div class="goods-info">
                <div class="goods-name">{{ scope.row.goodsName || '商品已删除' }}</div>
                <div class="goods-sub">ID: {{ scope.row.goodsId }} · 当前状态：{{ scope.row.goodsStatus || '—' }}</div>
              </div>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="reason" label="举报原因" width="130">
          <template slot-scope="scope">
            <el-tag type="danger" size="small">{{ scope.row.reason }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="补充说明" min-width="200">
          <template slot-scope="scope">
            <span class="detail-text">{{ scope.row.detail || '—' }}</span>
          </template>
        </el-table-column>
        <el-table-column label="举报人" width="150">
          <template slot-scope="scope">
            <div>{{ scope.row.reporterName || '未知' }}</div>
            <div class="goods-sub">{{ scope.row.reporterPhone || '未留电话' }}</div>
          </template>
        </el-table-column>
        <el-table-column label="举报时间" width="160">
          <template slot-scope="scope">{{ formatTime(scope.row.createdAt) }}</template>
        </el-table-column>
        <el-table-column label="状态" width="100">
          <template slot-scope="scope">
            <el-tag :type="statusTag(scope.row.status)" size="small">{{ scope.row.status }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="230" fixed="right">
          <template slot-scope="scope">
            <template v-if="scope.row.status === '待处理'">
              <el-button type="text" class="danger" @click="handle(scope.row, 'offline')">下架商品</el-button>
              <el-button type="text" class="ok" @click="handle(scope.row, 'handle')">标记已处理</el-button>
              <el-button type="text" @click="handle(scope.row, 'ignore')">忽略</el-button>
            </template>
            <span v-else class="done-time">{{ formatTime(scope.row.handledAt) }}</span>
          </template>
        </el-table-column>
      </el-table>

      <el-pagination
        class="pager"
        background
        layout="total, prev, pager, next"
        :total="total"
        :page-size="pageSize"
        :current-page.sync="page"
        @current-change="reload"
      />
    </el-card>
  </section>
</template>

<script>
import request from '@/api/request';

export default {
  data() {
    return {
      loading: false,
      acting: false,
      rows: [],
      total: 0,
      stats: {pending:0,handled:0,ignored:0,total:0},
      searchTimer: null,
      requestId: 0,
      keyword: '',
      statusFilter: '待处理',
      page: 1,
      pageSize: 10,
      tabs: [
        { label: '待处理', value: '待处理' },
        { label: '全部', value: '' },
        { label: '已处理', value: '已处理' },
        { label: '已忽略', value: '已忽略' }
      ]
    };
  },
  computed: {
    statCards() {
      return [
        { key: 'pending', label: '待处理', note: '需要核实', tab: '待处理' },
        { key: 'handled', label: '已处理', note: '已核实', tab: '已处理' },
        { key: 'ignored', label: '已忽略', note: '举报不成立', tab: '已忽略' },
        { key: 'total', label: '举报总数', note: '全部记录', tab: '' }
      ];
    },
    pagedRows() { return this.rows; }
  },
  watch: {
    keyword() {
      this.page = 1;
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => this.reload(), 300);
    }
  },
  beforeDestroy() { clearTimeout(this.searchTimer); this.requestId++; },
  created() {
    this.reload();
  },
  methods: {
    async reload() {
      this.loading = true;
      const requestId=++this.requestId;
      try {
        const res = await request.get('/admin/reports', { params: { status: this.statusFilter,keyword:this.keyword,page:this.page,pageSize:this.pageSize } });
        if(requestId!==this.requestId)return;
        this.total=res.data.total;
        this.stats=res.data.stats;
        this.rows = (res.data && res.data.list) || [];
      } catch (error) {
        this.$message.error('举报列表加载失败，请重新登录后再试');
      } finally {
        if(requestId===this.requestId)this.loading = false;
      }
    },
    switchTab(value) {
      this.statusFilter = value;
      this.page = 1;
      this.reload();
    },
    formatTime(value) {
      if (!value) return '—';
      const date = new Date(String(value).replace(' ', 'T'));
      if (Number.isNaN(date.getTime())) return String(value).slice(0, 19);
      const pad = (n) => String(n).padStart(2, '0');
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
    },
    statusTag(status) {
      if (status === '待处理') return 'warning';
      if (status === '已处理') return 'success';
      return 'info';
    },
    previewImg(url) {
      if (!url) return;
      if (this.$viewerApi) this.$viewerApi({ images: [url], url });
      else window.open(url);
    },
    handle(row, action) {
      const map = {
        offline: { text: `确认下架「${row.goodsName}」并把该举报标记为已处理？`, confirm: '下架商品', method: '下架并处理' },
        handle: { text: '确认把该举报标记为已处理？商品状态不会改变。', confirm: '标记已处理', method: '标记处理' },
        ignore: { text: '确认忽略该举报？商品与举报记录都会保留。', confirm: '忽略举报', method: '忽略' }
      };
      const config = map[action];
      this.$confirm(config.text, '举报处理', { type: 'warning', confirmButtonText: config.confirm })
        .then(async () => {
          if (this.acting) return;
          this.acting = true;
          try {
            await request.put(`/admin/reports/${row.id}`, { action });
            this.$message.success(`已${config.method}`);
            await this.reload();
          } catch (error) {
            this.$message.error((error.response && error.response.data && error.response.data.message) || '操作失败，请重试');
          } finally {
            this.acting = false;
          }
        })
        .catch(() => {});
    }
  }
};
</script>

<style scoped>
.report-page { padding: 0 4px; }
.hero { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 18px; }
.hero h1 { margin: 0; color: #24384b; font-size: 22px; font-weight: 600; }
.hero p { margin: 8px 0 0; color: #8493a1; font-size: 13px; }
.stat-row { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; margin-bottom: 16px; }
.stat-card { display: flex; flex-direction: column; gap: 6px; padding: 16px 18px; border-radius: 10px; background: #fff; border-left: 4px solid #d9e2ea; cursor: pointer; transition: box-shadow .2s; }
.stat-card:hover { box-shadow: 0 6px 18px rgba(36, 56, 75, .08); }
.stat-card.pending { border-left-color: #e6a23c; }
.stat-card.handled { border-left-color: #2f9e6d; }
.stat-card.ignored { border-left-color: #a6b2bd; }
.stat-card.total { border-left-color: #5b8ff9; }
.stat-label { color: #8493a1; font-size: 13px; }
.stat-value { color: #24384b; font-size: 26px; line-height: 1.2; font-weight: 700; }
.stat-note { color: #a6b2bd; font-size: 12px; }
.table-card { border-radius: 10px; }
.toolbar { display: flex; align-items: center; justify-content: space-between; margin-bottom: 14px; }
.tabs { display: flex; gap: 8px; }
.tab { padding: 7px 16px; border-radius: 16px; color: #5b6b78; background: #f2f5f8; font-size: 13px; cursor: pointer; }
.tab.active { color: #fff; background: #28745b; }
.tab em { margin-left: 6px; padding: 0 6px; border-radius: 9px; background: #e6a23c; color: #fff; font-size: 11px; font-style: normal; }
.goods-cell { display: flex; align-items: center; gap: 10px; }
.thumb { width: 56px; height: 44px; object-fit: cover; border-radius: 4px; cursor: zoom-in; background: #f2f5f8; }
.goods-name { color: #24384b; font-weight: 600; }
.goods-sub { margin-top: 4px; color: #94a1ad; font-size: 12px; }
.detail-text { color: #5b6b78; word-break: break-all; }
.danger { color: #e05d5d; }
.ok { color: #28745b; font-weight: 600; }
.done-time { color: #a6b2bd; font-size: 12px; }
.pager { margin-top: 16px; text-align: right; }
</style>
